import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { User } from "@px7/core";
import { api, ApiError } from "./api";

type Impersonator = { id: string; name: string };

type AuthState = {
  user: User | null;
  /** Admin who impersonated the current user. Null when not impersonating. */
  impersonator: Impersonator | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  stopImpersonation: () => Promise<void>;
};

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [impersonator, setImpersonator] = useState<Impersonator | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    try {
      const { user, impersonator } = await api<{ user: User; impersonator: Impersonator | null }>("/auth/me");
      setUser(user);
      setImpersonator(impersonator ?? null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setUser(null);
        setImpersonator(null);
      } else {
        throw e;
      }
    }
  }

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  async function login(email: string, password: string) {
    const { user } = await api<{ user: User }>("/auth/login", {
      method: "POST",
      json: { email, password },
    });
    setUser(user);
    setImpersonator(null);
  }

  async function logout() {
    await api("/auth/logout", { method: "POST" });
    setUser(null);
    setImpersonator(null);
  }

  async function stopImpersonation() {
    await api("/auth/stop-impersonation", { method: "POST" });
    await refresh();
    // Force a hard navigation so route-level role guards re-evaluate cleanly.
    window.location.href = "/coach/admin/users";
  }

  return (
    <Ctx.Provider value={{ user, impersonator, loading, login, logout, refresh, stopImpersonation }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
